import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import api, { apiErr, fmtMoney, fmtDate, API_BASE } from "@/lib/api";
import { PIPELINE_STAGES, INTEREST } from "@/lib/constants";
import { PageHeader, Loader, EmptyState } from "@/components/ui-kit";
import { StatusBadge } from "@/components/StatusBadge";
import EntityForm from "@/components/EntityForm";
import ProspectDetailDialog from "@/components/ProspectDetailDialog";
import BulkConvertDialog from "@/components/BulkConvertDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Users2, MoreVertical, Pencil, Trash2, Download, Upload, Search, MessageSquareText, FileDown, ArrowRightLeft, Contact, UserCheck, BadgeCheck } from "lucide-react";
import { toast } from "sonner";

export default function Prospects() {
  const [items, setItems] = useState(null);
  const [settings, setSettings] = useState({});
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [q, setQ] = useState("");
  const [commercials, setCommercials] = useState([]);
  const [transfer, setTransfer] = useState(null);
  const [transferTo, setTransferTo] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);
  const fileRef = useRef();

  const load = () => api.get("/prospects").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/settings").then((r) => setSettings(r.data)); api.get("/team/commercials").then((r) => setCommercials(r.data)).catch(() => {}); }, []);

  const schema = [
    { name: "nom", label: "Nom", required: true },
    { name: "prenom", label: "Prénom" },
    { name: "entreprise", label: "Entreprise" },
    { name: "telephone", label: "Téléphone" },
    { name: "whatsapp", label: "WhatsApp" },
    { name: "email", label: "Email", type: "email" },
    { name: "pays", label: "Pays", type: "select", options: settings.countries || [] },
    { name: "ville", label: "Ville", type: "select", options: settings.cities || [] },
    { name: "adresse", label: "Adresse", col: 2 },
    { name: "type_prospect", label: "Type", type: "select", options: settings.prospect_types || [] },
    { name: "source", label: "Source", type: "select", options: settings.sources || [] },
    { name: "stage", label: "Étape pipeline", type: "select", options: settings.pipeline_stages || PIPELINE_STAGES },
    { name: "niveau_interet", label: "Niveau d'intérêt", type: "select", options: settings.interest_levels || INTEREST },
    { name: "budget", label: "Budget (FCFA)", type: "number" },
    { name: "prochaine_action", label: "Prochaine action" },
    { name: "commentaires", label: "Commentaires", type: "textarea" },
  ];

  const save = async (form) => {
    try {
      if (editing) { await api.put(`/prospects/${editing.id}`, form); toast.success("Prospect mis à jour"); }
      else { await api.post("/prospects", form); toast.success("Prospect créé"); }
      load();
    } catch (e) { toast.error(apiErr(e)); throw e; }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce prospect ?")) return;
    await api.delete(`/prospects/${id}`); toast.success("Supprimé"); load();
  };

  const patchStage = async (p, stage) => {
    try { const { data } = await api.patch(`/prospects/${p.id}/stage`, { stage }); setItems((prev) => prev.map((x) => (x.id === p.id ? data : x))); toast.success("Étape mise à jour"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const convert = async (p) => {
    if (!window.confirm(`Convertir « ${p.nom} ${p.prenom || ""} » en client ?`)) return;
    try {
      const { data } = await api.post(`/prospects/${p.id}/convert-to-client`);
      toast.success(data.linked ? `Prospect relié au client existant « ${data.client.nom} »` : `Client « ${data.client.nom} » créé`);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const doTransfer = async () => {
    if (!transferTo) return;
    try {
      await api.patch(`/prospects/${transfer.id}/transfer`, { owner_id: transferTo });
      toast.success("Prospect transféré"); setTransfer(null); setTransferTo(""); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const exportFile = (format) => {
    const token = localStorage.getItem("omnibat_token");
    fetch(`${API_BASE}/prospects/export?format=${format}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob()).then((b) => {
        const url = URL.createObjectURL(b); const a = document.createElement("a");
        a.href = url; a.download = `prospects.${format === "excel" ? "xlsx" : "csv"}`; a.click();
      });
  };

  const importFile = async (e) => {
    const f = e.target.files[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post("/prospects/import", fd);
      toast.success(`${data.imported} importés · ${data.duplicates} doublons ignorés`);
      load();
    } catch (err) { toast.error(apiErr(err)); }
    e.target.value = "";
  };

  const downloadTemplate = () => {
    const headers = "nom,prenom,entreprise,telephone,whatsapp,email,pays,ville,type_prospect,source";
    const sample = "Dupont,Jean,ACME BTP,+22890000000,+22890000000,jean@acme.com,Togo,Lomé,entreprise,Salon";
    const blob = new Blob([`${headers}\n${sample}\n`], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob); const a = document.createElement("a");
    a.href = url; a.download = "modele_prospects.csv"; a.click();
  };

  const wonToConvert = (items || []).filter((p) => p.stage === "Gagné" && !p.client_id);
  const filtered = (items || []).filter((p) =>
    !q || `${p.nom} ${p.prenom} ${p.entreprise} ${p.telephone} ${p.email}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <PageHeader title="Prospects" subtitle="CRM commercial — centralisation des prospects & clients">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" data-testid="export-menu"><Download className="h-4 w-4 mr-1.5" />Exporter</Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => exportFile("excel")} data-testid="export-excel"><Download className="h-4 w-4 mr-2" />Excel (.xlsx)</DropdownMenuItem>
            <DropdownMenuItem onClick={() => exportFile("csv")} data-testid="export-csv"><Download className="h-4 w-4 mr-2" />CSV (.csv)</DropdownMenuItem>
            <DropdownMenuItem onClick={downloadTemplate} data-testid="download-template"><FileDown className="h-4 w-4 mr-2" />Modèle d'import</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <Button variant="outline" onClick={() => fileRef.current.click()} data-testid="import-btn"><Upload className="h-4 w-4 mr-1.5" />Importer</Button>
        <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" hidden onChange={importFile} />
        {wonToConvert.length > 0 && <Button variant="outline" onClick={() => setBulkOpen(true)} className="border-emerald-600 text-emerald-700 hover:bg-emerald-50" data-testid="bulk-convert-btn"><UserCheck className="h-4 w-4 mr-1.5" />Convertir les gagnés ({wonToConvert.length})</Button>}
        <Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="add-prospect" className="bg-brand-600 hover:bg-brand-700 text-white"><Plus className="h-4 w-4 mr-1.5" />Nouveau prospect</Button>
      </PageHeader>

      <div className="relative mb-4 max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filtrer…" className="pl-9" data-testid="prospect-filter" />
      </div>

      {items === null ? <Loader /> : filtered.length === 0 ? (
        <EmptyState icon={Users2} title="Aucun prospect" subtitle="Créez votre premier prospect ou importez un fichier Excel/CSV."
          action={<Button onClick={() => { setEditing(null); setOpen(true); }} className="bg-brand-600 text-white"><Plus className="h-4 w-4 mr-1.5" />Nouveau prospect</Button>} />
      ) : (
        <div className="rounded-xl border bg-white overflow-hidden">
          <div className="overflow-x-auto scrollbar-thin">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>N°</TableHead><TableHead>Nom</TableHead><TableHead>Entreprise</TableHead>
                  <TableHead>Téléphone</TableHead><TableHead>Étape</TableHead><TableHead>Intérêt</TableHead>
                  <TableHead>Budget</TableHead><TableHead>Commercial</TableHead><TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((p) => (
                  <TableRow key={p.id} data-testid={`prospect-row-${p.id}`} className="hover:bg-slate-50">
                    <TableCell className="font-mono text-xs text-slate-500">{p.numero}</TableCell>
                    <TableCell className="font-semibold">
                      {p.nom} {p.prenom}
                      {p.client_id && <Link to={`/clients?id=${p.client_id}`} className="ml-2 inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-700 hover:bg-emerald-200" data-testid={`prospect-client-badge-${p.id}`}><BadgeCheck className="h-3 w-3" />Client</Link>}
                    </TableCell>
                    <TableCell className="text-slate-600">{p.entreprise || "—"}</TableCell>
                    <TableCell className="font-mono text-xs">{p.telephone || "—"}</TableCell>
                    <TableCell className="min-w-44" onClick={(e) => e.stopPropagation()}>
                      <Select value={p.stage} onValueChange={(v) => patchStage(p, v)}>
                        <SelectTrigger className="h-8 text-xs" data-testid={`prospect-stage-${p.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{(settings.pipeline_stages || PIPELINE_STAGES).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell><StatusBadge value={p.niveau_interet} /></TableCell>
                    <TableCell className="font-mono text-xs">{fmtMoney(p.budget)}</TableCell>
                    <TableCell className="text-slate-600 text-sm">{p.owner_name}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`prospect-menu-${p.id}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setDetail(p)} data-testid={`prospect-fiche-${p.id}`}><Contact className="h-4 w-4 mr-2" />Fiche prospect</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setDetail(p)} data-testid={`prospect-comments-${p.id}`}><MessageSquareText className="h-4 w-4 mr-2" />Suivi &amp; commentaires</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => { setEditing(p); setOpen(true); }}><Pencil className="h-4 w-4 mr-2" />Modifier</DropdownMenuItem>
                          {p.stage === "Gagné" && !p.client_id && <DropdownMenuItem onClick={() => convert(p)} className="text-emerald-700" data-testid={`prospect-convert-${p.id}`}><UserCheck className="h-4 w-4 mr-2" />Convertir en client</DropdownMenuItem>}
                          <DropdownMenuItem onClick={() => { setTransfer(p); setTransferTo(""); }} data-testid={`prospect-transfer-${p.id}`}><ArrowRightLeft className="h-4 w-4 mr-2" />Transférer / Céder</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => remove(p.id)} className="text-rose-600"><Trash2 className="h-4 w-4 mr-2" />Supprimer</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      <EntityForm open={open} onOpenChange={setOpen} title={editing ? "Modifier le prospect" : "Nouveau prospect"}
        schema={schema} initial={editing} onSubmit={save} />

      <BulkConvertDialog prospects={wonToConvert} open={bulkOpen} onOpenChange={setBulkOpen} onDone={load} />
      <ProspectDetailDialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)} prospect={detail}
        stages={settings.pipeline_stages || PIPELINE_STAGES}
        onUpdated={(up) => { setDetail(up); setItems((prev) => prev.map((x) => (x.id === up.id ? up : x))); }} />

      <Dialog open={!!transfer} onOpenChange={(o) => !o && setTransfer(null)}>
        <DialogContent className="max-w-md" data-testid="transfer-dialog">
          <DialogHeader><DialogTitle className="font-head">Transférer le prospect</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Céder <span className="font-semibold text-slate-700">{transfer?.nom} {transfer?.prenom}</span> à un autre commercial.</p>
          <Select value={transferTo} onValueChange={setTransferTo}>
            <SelectTrigger data-testid="transfer-select" className="mt-1"><SelectValue placeholder="Choisir un commercial" /></SelectTrigger>
            <SelectContent>
              {commercials.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransfer(null)}>Annuler</Button>
            <Button onClick={doTransfer} disabled={!transferTo} data-testid="transfer-confirm" className="bg-brand-600 hover:bg-brand-700 text-white">Transférer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
